// Sugar Alarm — cycle 452: Log as an overlay on `/now`, as a real hook.
//
// It lives here rather than inline in `App.tsx` for the reason cycle 451.1
// gave for `useAlarmRouteReturn`: a rule that decides whether a caregiver can
// log the carbs they are giving without losing the alarm controls has to be
// MOUNTABLE in a test. `logOverlay.test.ts` mounts THIS, in a real router.
//
// THE MODEL. While sustained alarm audio sounds, the Now screen's Log
// affordances call `openLogOverlay(to)` instead of navigating to `to`. The
// overlay is keyed on ONE same-URL history entry the opener pushes:
//
//   - the pathname never changes, so 451.1's pin never fires and there is no
//     route for Back to bounce into;
//   - Android's hardware Back is `history.back()` inside the WebView (there
//     is no native back override in either shell), so Back pops that entry
//     and closes the overlay — and never navigates;
//   - the overlay is open ONLY while the current entry carries its token, so
//     a reload (which forgets component state), Forward onto the entry, or
//     any move off `/now` cannot resurrect it.
//
// ⚠ NO URL STATE AND NOTHING PERSISTED. The intent (`?tab=…`) is held in
// component state and handed to `LogScreen` as a prop; the pushed entry's URL
// is `/now` exactly. No storage key, no query parameter.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { isNowPath } from "../lib/alarmControl";

/** The history-state key the opener tags its entry with. */
export const LOG_OVERLAY_STATE_KEY = "sgLogOverlay";

export interface LogOverlayController {
  /** The overlay is on screen. */
  open: boolean;
  /** The query part of the Log target it was opened with (`""` for bare
   *  Log), or null when closed. */
  search: string | null;
  /** Open Log over `/now` for a `/log…` target, without navigating. */
  openLogOverlay: (to: string) => void;
  /** Close it (pops the entry the opener pushed). */
  closeLogOverlay: () => void;
}

export function useLogOverlay(): LogOverlayController {
  const location = useLocation();
  const nav = useNavigate();
  const [state, setState] = useState<{ search: string; token: string } | null>(
    null,
  );

  const entryToken = (
    location.state as Record<string, unknown> | null
  )?.[LOG_OVERLAY_STATE_KEY];
  const open =
    state != null &&
    isNowPath(location.pathname) &&
    entryToken === state.token;

  // Drop the state once its entry is gone (Back, Close, any navigation) —
  // but only after it has actually been seen open, so the render between
  // `setState` and the router landing on the pushed entry cannot clear it.
  const seenOpenRef = useRef(false);
  useEffect(() => {
    if (state == null) {
      seenOpenRef.current = false;
      return;
    }
    if (open) {
      seenOpenRef.current = true;
      return;
    }
    if (seenOpenRef.current) setState(null);
  }, [state, open]);

  // Mirrors `state` synchronously, so a second open in the same tick (a
  // double tap before React re-renders) sees the first one. Without it each
  // tap pushed its own entry and Close / Back popped only one, leaving a
  // stale same-URL entry that swallowed the next Back.
  const stateRef = useRef(state);
  stateRef.current = state;

  const openLogOverlay = useCallback(
    (to: string) => {
      const q = to.indexOf("?");
      const search = q >= 0 ? to.slice(q) : "";
      const current = stateRef.current;
      // Open, or opening (set but not yet seen open): never push a second
      // entry. A state that WAS seen open and is no longer is a closed
      // overlay the cleanup effect has not dropped yet, so a fresh open.
      if (current != null && (open || !seenOpenRef.current)) {
        const next = { ...current, search };
        stateRef.current = next;
        setState(next);
        return;
      }
      const token = `${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const next = { search, token };
      stateRef.current = next;
      setState(next);
      // A PUSH (not a replace): Back must have an entry to pop. Same URL.
      nav(location.pathname + location.search, {
        state: { [LOG_OVERLAY_STATE_KEY]: token },
      });
    },
    [nav, location.pathname, location.search, open],
  );

  const closeLogOverlay = useCallback(() => {
    if (open) nav(-1);
    else setState(null);
  }, [open, nav]);

  return {
    open,
    search: open && state != null ? state.search : null,
    openLogOverlay,
    closeLogOverlay,
  };
}
